import { IOptionItem } from '@wyz/types';
import {
  Alert,
  Card,
  CenteredSpinner,
  ErrorMessage,
  Select,
  Title,
} from '@wyz/ui';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { get } from 'lodash-es';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useManageSupplierPlatforms } from '~/common/api';
import { Trans } from '~/common/context';
import { InternalStocksDraggables } from '~/connect/components';
import {
  CustomerFormProps,
  ICustomerInternalStocksForm,
} from '~/connect/types';

export default function InternalStocksForm({ isWriting }: CustomerFormProps) {
  const methods = useFormContext<ICustomerInternalStocksForm>();
  const internalStocksMethods = useFieldArray({
    name: 'internalStocks',
    keyName: 'key',
    control: methods.control,
  });
  const watchInternalStocks = useWatch({
    name: 'internalStocks',
    control: methods.control,
  });
  // Set by the API's validation errors (see use-customer-form), already translated. `get` because
  // `internalStocks` is a field-array path, so the error is not typed as a plain FieldError.
  const stocksError: string | undefined = get(
    methods.formState.errors,
    'internalStocks',
  )?.message;

  const manageSupplierPlatforms = useManageSupplierPlatforms({
    list: {
      enabled: true,
      args: {
        supplierTypeIds: [SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR],
      },
    },
  });

  return (
    <Card>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Trans i18nKey='app.entity.tab.my_stocks' />
        </Title>
      </Card.Header>
      <Card.Body className='space-y-2'>
        <Select<false, IOptionItem<number, string>>
          name='supplierPlatform'
          placeholder={
            <Trans i18nKey='app.entity.form.my_stocks.supplier_selector.label' />
          }
          value={null}
          onChange={(selected) => {
            if (selected) {
              internalStocksMethods.append({
                id: selected.value,
                name: selected.label,
              });
            }
          }}
          options={
            manageSupplierPlatforms.list.data?.data.map((supplierPlatform) => ({
              label: supplierPlatform.supplier.name,
              value: supplierPlatform.supplier.id,
            })) ?? []
          }
          isOptionDisabled={(option) =>
            watchInternalStocks.some((stock) => stock.id === option.value)
          }
          isDisabled={!isWriting}
          isLoading={manageSupplierPlatforms.list.isProcessing}
          size='sm'
        />
        {!manageSupplierPlatforms.list.isSettled && <CenteredSpinner />}
        {manageSupplierPlatforms.list.isSuccess &&
          watchInternalStocks.length === 0 && (
            <Alert variant='warning'>
              <Trans i18nKey='app.entity.form.my_stocks.empty_message' />
            </Alert>
          )}
        {manageSupplierPlatforms.list.isSuccess &&
          watchInternalStocks.length > 0 && (
            <div className='bg-neutral-grey-1 border rounded-3 border-neutral-grey-4 p-3'>
              <InternalStocksDraggables
                suppliers={watchInternalStocks}
                onMove={(indexA, indexB) => {
                  internalStocksMethods.move(indexA, indexB);
                }}
                onRemove={(index) => {
                  internalStocksMethods.remove(index);
                }}
                mode='WRITE'
              />
            </div>
          )}
        {stocksError && <ErrorMessage message={stocksError} />}
      </Card.Body>
    </Card>
  );
}
